import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { DataType, IAgenda, IAgendaStep, IMeeting, MeetingType, MeetingCategory, UserRole, MeetingAttendee, Services } from '../types';
import { Item, User, Agenda } from './';
import { Option } from "src/components";
import i18next from 'src/i18n';
import { rootStore } from 'src/app';
import client from 'src/feathers';
import { getUnixTime, isToday, addHours, addMinutes, differenceInMinutes, isBefore } from "date-fns";
import { computedFn } from 'mobx-utils';
import { parse } from "date-fns";


export class Meeting extends Item<IMeeting> implements IMeeting {
  agenda_id?:       number = null;
  agenda?:          Agenda = null;
  agenda_step?:     number = null;
  category?:        MeetingCategory = null;
  duration:         number  = null;
  ics_sequence:     number  = null;
  invitations:      boolean = null;
  leader_id:        number  = null;
  location:         string  = null;
  opened:           Date    = null;
  reminder:         boolean = null;
  reminder_minutes: number  = null;
  start:            Date    = null;
  type:             MeetingType = MeetingType.GoogleMeeting;
  url:              string  = null;
  password?:        string  = null;

  protected constructor(data: IMeeting) {
    // The super constructor takes care of references to the service, validation rules
    // and intialized the base members with mobx decorators
    super(DataType.Meeting, data);

    // Since Mobx cannot track derived class objects automatically,
    // the decorators have to be provided manually 
    makeObservable(this, {
      agenda:           observable,
      agenda_id:        observable,
      agenda_step:      observable,
      category:         observable,
      duration:         observable,
      ics_sequence:     observable,
      invitations:      observable,
      leader_id:        observable,
      location:         observable,
      opened:           observable,
      reminder:         observable,
      reminder_minutes: observable,
      start:            observable,
      type:             observable,
      url:              observable,
      password:         observable,

      getLeader:            computed,
      getNextAgendaStep:    computed,
      getCurrentAgendaStep: computed,
      getPrevAgendaStep:    computed,

      startMeeting:       action,
      stopMeeting:        action,
      goToPrevAgendaStep: action,
      goToNextAgendaStep: action,
      createAgenda:       action,
      loadAgenda:         action,
    });

    // Initialize data
    // While the super constructor will call this once,
    // the default values given to members will overwrite this so it has to be called again here. 
    this.fromData(data);
  }

  /*******************************************************************************************************************************************
   * Overwrites
   ********************************************************************************************************************************************/
  public static create(data?: IMeeting): Meeting {
    
    const date    = new Date();
    const hours   = date.getHours();
    const minutes = date.getMinutes();
    const defaultDate = parse(`${hours}:${minutes}`, "HH:mm", new Date());

    return new Meeting(data || {
      ...Item.create(data).toData(),
      date:  defaultDate,
      start: defaultDate,
      leader_id: rootStore.authStore.getCurUserID,
      duration: 1,
      reminder_minutes: 15,
    });
  }

  protected fromData(data: IMeeting): void {
    super.fromData(data);
    this.date             = data.date && new Date(data.date);
    this.category         = data.category;
    this.agenda_id        = data.agenda_id && Number(data.agenda_id);
    this.agenda_step      = data.agenda_step && Number(data.agenda_step);
    this.duration         = data.duration && Number(data.duration);
    this.ics_sequence     = data.ics_sequence && Number(data.ics_sequence);
    this.invitations      = data.invitations;
    this.leader_id        = data.leader_id && Number(data.leader_id);
    this.location         = data.location;
    this.opened           = data.opened && new Date(data.opened);
    this.reminder         = data.reminder;
    this.reminder_minutes = data.reminder_minutes;
    this.start            = data.start && new Date(data.start);
    this.type             = data.type as MeetingType;
    this.url              = data.url;
    this.password         = data.password;
  }

  public toData(): any {
    return {
      ...super.toData(),
      agenda_step:      this.agenda_step,
      agenda_id:        this.agenda_id,
      category:         this.category,
      duration:         this.duration,
      ics_sequence:     this.ics_sequence,
      invitations:      this.invitations,
      leader_id:        this.leader_id,
      location:         this.location,
      date:             this.date && this.date.toISOString(),
      opened:           this.opened && this.opened.toISOString(),
      reminder:         this.reminder,
      reminder_minutes: this.reminder_minutes,
      start:            this.start && this.start.toISOString(),
      type:             this.type,
      url:              this.url,
      password:         this.password,
    };
  }

  public toPatchData(patch: Partial<IMeeting>): any {
    return {
      ...super.toPatchData(patch),
      agenda_id:        "agenda_id" in patch ? patch.agenda_id:                         undefined,
      agenda_step:      "agenda_step" ? patch.agenda_step:                              undefined,
      category:         "category" in patch ? patch.category:                           undefined,
      duration:         "duration" in patch ? patch.duration:                           undefined,
      ics_sequence:     "ics_sequence" in patch ? patch.ics_sequence:                   undefined,
      invitations:      "invitations" in patch ? patch.invitations:                     undefined,
      leader_id:        "leader_id" in patch ? patch.leader_id:                         undefined,
      location:         "location" in patch ? patch.location:                           undefined,
      opened:           "opened" in patch ? patch.opened && patch.opened.toISOString(): undefined,
      reminder:         "reminder" in patch ? patch.reminder:                           undefined,
      reminder_minutes: "reminder_minutes" in patch ? patch.reminder_minutes:           undefined,
      date:             "date" in patch ? patch.date && patch.date.toISOString():       undefined,
      start:            "start" in patch ? patch.start && patch.start.toISOString():    undefined,
      type:             "type" in patch ? patch.type:                                   undefined,
      url:              "url" in patch ? patch.url:                                     undefined,
      password:         "password" in patch ? patch.password:                           undefined,
    };
  }


  public get canEdit() : boolean {
    return super.canEdit
    && ([UserRole.Leader, UserRole.Owner].includes(rootStore.authStore.getCurUser.getRole(this.room_id))
    || this.leader_id == rootStore.authStore.getCurUser.id
    || this.created_by == rootStore.authStore.getCurUser.id)
  }

  public canEditField = computedFn((field?: keyof IMeeting): boolean => {
    return this.canEdit;
  })

  /*******************************************************************************************************************************************
   * Setter
   ********************************************************************************************************************************************/
  /*******************************************************************************************************************************************
   * Getter
   ********************************************************************************************************************************************/
  public static get getCategories(): Option[] {
    return Object.values(MeetingCategory).map(c => ({ label: i18next.t(c), value: c }));
  }

  get getLeader(): User | null {
    return this.leader_id && rootStore.userStore.getItemByID(this.leader_id) ? rootStore.userStore.getItemByID(this.leader_id) : null;
  }

  get isActive(): boolean {
    return rootStore.spaceStore.getItemByID(this.room_id) && rootStore.spaceStore.getItemByID(this.room_id).active_meeting_id === this.id;
  }

  get canBeOpened(): boolean {
    return differenceInMinutes(addHours(this.start, this.duration), new Date()) > 0;
  }

  get hasPassed(): boolean {
    return !isBefore(addHours(this.start, this.duration), new Date());
  }

  get isToday(): boolean {
    return isToday(this.start);
  }

  get getPrevAgendaStep(): IAgendaStep {
    if (!this.isActive || !this.getAgenda) return null;
    const current_step_index = this.getAgenda.steps.findIndex(step => Number(step.index) === Number(this.agenda_step))
    return current_step_index > 0 && this.getAgenda.steps[current_step_index - 1] ? this.getAgenda.steps[current_step_index - 1] : null
  }

  get getCurrentAgendaStep(): IAgendaStep {
    if (!this.isActive || !this.getAgenda) return null;
    if (!this.agenda || !this.getAgenda.steps.length) return null;
    return this.getAgenda.steps[this.agenda_step||0]
  }

  get getNextAgendaStep(): IAgendaStep {
    if (!this.isActive || !this.getAgenda) return null;
    const current_step_index = this.getAgenda.steps.findIndex(step => Number(step.index) === Number(this.agenda_step));
    return current_step_index + 1 <= this.getAgenda.steps.length && this.getAgenda.steps[current_step_index + 1] ? this.getAgenda.steps[current_step_index + 1] : null;
  }

  get getAgenda(): Agenda {
    return this.agenda;
  }

  /*******************************************************************************************************************************************
   * Actions
   ********************************************************************************************************************************************/

  /**
   * Start current meeting by setting it as active on the room the meeting belongs to. This will overwrite 
   * any other meeting that was set as active.
   */
  public startMeeting = async (): Promise<void> => {
    if(this.agenda_id && !this.agenda) await this.loadAgenda();
    await rootStore.spaceStore.getItemByID(this.room_id).set("active_meeting_id", this.id, true);
    if(!this.getAgenda) return;
    if (this.getAgenda && this.getAgenda.steps.length && !this.agenda_step) {
      await this.set('agenda_step', this.getAgenda.steps[0].index, true);
    }
  }

  public stopMeeting = async (): Promise<void> => {
    if (this.isActive) await rootStore.spaceStore.getItemByID(this.room_id).set("active_meeting_id", null, true);
    rootStore.meetingStore.setActiveMeeting(null);
    if(!this.getAgenda) return;
    if(this.agenda_step == this.getAgenda.steps[this.getAgenda.steps.length-1].index ) {
      console.log('reset')
      await this.set('agenda_step', this.getAgenda.steps[0].index, true);
    }
  }

  public goToPrevAgendaStep = async (): Promise<void> => {
    if (!this.isActive) return;
    const prev_step = this.getPrevAgendaStep;
    if (prev_step && prev_step.index != this.agenda_step) {
      await this.set('agenda_step', prev_step.index, true);
    }
  }

  public goToNextAgendaStep = async (): Promise<void> => {
    if (!this.isActive) return;
    const next_step = this.getNextAgendaStep;
    if (next_step && next_step.index != this.agenda_step) {
      await this.set('agenda_step', next_step.index, true);
    }
  }

  public createAgenda = async (agendaTemplate?: IAgendaStep[]): Promise<IAgenda | null> => {
    try {
      const data: IAgenda = { id: null, steps: agendaTemplate || [], workspace_id: this.workspace_id, title: this.title + ' ' + 'agenda' };
      const agenda = Agenda.create(data);
      await agenda.save()
      await agenda.linkSpace(this.room_id);
      this.set('agenda_id', agenda.id, true);
      this.agenda = agenda;
    } catch (error) {
      console.error('Fail to generate agenda for meeting', error)
    }
    return null;
  }

  public loadAgenda = async (): Promise<void> => {
    if(!this.agenda_id || this.agenda) return;
    try {
      console.log('load agenda')
      const agenda = await client.service('agenda').get(this.agenda_id);

      runInAction(() => {
        this.agenda = Agenda.create(agenda)
      })
    } catch (error) {
      console.error('Fail to load meeting agenda', error);
    }
  }

  public sendInvitations = async (): Promise<number> => {
    try {
      const send = await client.service(Services.MeetingInvitation).create(this.toData());
      return send.invitations;
    } catch (error) {
      console.error('Fail to send meeting invitations', error);
      return 0;
    }
  }

  public icsExport = async (): Promise<any> => {
    try {
      const file = await client.service(Services.MeetingICS).create(this.toData());
      const element = document.createElement('a');
      element.setAttribute('href', 'data:text/calendar;charset=utf-8,' + encodeURIComponent(file.value));
      element.setAttribute('download', this.title + '.ics');
      element.style.display = 'none';
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
    } catch (error) {
      console.error('Fail to generate meeting ics', error)
    }
  }

  public validate = async (asBoolean?: boolean): Promise<any> => {
    let parentInvalids = await super.validate();
    if(this.isNew)  return [];
    if(this.opened) return [];

    // type this object, make generic for items and let each items contribute to list of required fields
    const obj: any = {
      start: this.start,
      duration: this.duration,
    };
    
    const itemInvalids = Object.keys(obj).filter((k) => {
      return !obj[k];
    });

    const invalids = [...parentInvalids, ...itemInvalids]

    return asBoolean ? invalids.length : invalids;
  }

}